import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ActivityType, RelatedToEnum } from 'ui/types/activities';

import { PAGES } from '~/constants/pages.constants';
import { useActivityCategories } from '~/hooks/useActivityCategories';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useLoadMeasurementDefinitionById } from '~/hooks/useMeasurementDefinition/useLoadMeasurementDefinitionById';
import { useRouter } from '~/hooks/useRouter';
import { IOffPlatformDefinitionFormFields } from '~/pages/Authenticated/OffPlatformApplication/components/DefinitionForm/types';
import { sections } from '~/pages/Authenticated/OffPlatformApplication/constants';
import { useMeasurementUnitsController } from '~/pages/Authenticated/OffPlatformApplication/controllers/useMeasurementUnitsController';
import {
  buildCauseOptions,
  createMeasurementsDto,
  getInitialLogFields,
  mapCauseOptions,
  mapCauseOptionsToDto,
} from '~/pages/Authenticated/OffPlatformApplication/utils';
import MeasurementDefinitionService from '~/services/resources/measurementDefinition';
import { ICategoryDTO } from '~/types/dtos/measurement';
import { CreateOffPlatformMeasurementDefinitionDTO } from '~/types/dtos/measurement-definition';
import { getAddressFromLocation } from '~/utils/getLocation';

export const useOffPlatformApplicationController = () => {
  // Providers
  const navigate = useNavigate();
  const { params, goToRoute } = useRouter();
  const { selectedUserProfile } = useAppSelector(({ auth }) => auth);
  const { selectedEcosystem } = useAppSelector(({ ecosystem }) => ecosystem);

  // Hooks
  const { measurementUnitGroupedOptions, mapMeasurementUnitFromSelectOption } =
    useMeasurementUnitsController();

  const { data: causeOptionsData } = useActivityCategories(
    RelatedToEnum.CAUSE_OPTIONS,
  );

  const {
    data: measurementDefinition,
    isLoading: isLoadingMeasurementDefinition,
  } = useLoadMeasurementDefinitionById(params.definitionId);

  // States
  const [isLoading, setIsLoading] = useState(false);
  const [sectionIndex, setSectionIndex] = useState(params.definitionId ? 1 : 0);

  // Derived data
  const measurementsUnits = useMemo(
    () =>
      mapMeasurementUnitFromSelectOption(
        selectedEcosystem?.measurementUnits?.map((unit) => ({
          value: unit._id,
          label: unit.pluralLabel,
          payload: unit,
        })) ?? [],
      ),
    [selectedEcosystem?.measurementUnits, mapMeasurementUnitFromSelectOption],
  );

  const measurementDefaultValues = useMemo(
    () => getInitialLogFields(measurementsUnits),
    [measurementsUnits],
  );

  const definitionDefaultValues: IOffPlatformDefinitionFormFields =
    useMemo(() => {
      const baseDefaults: IOffPlatformDefinitionFormFields = {
        ...measurementDefaultValues,
        title: '',
        description: '',
        organizationName: '',
        numberOfParticipants: undefined,
        causeOptions: [],
        isRemote: false,
        location: undefined,
      };

      if (!measurementDefinition) return baseDefaults;

      const {
        titleOfActivity = '',
        description = '',
        organizationName = '',
        numberOfParticipants,
        causeOptions,
        isRemote = false,
        address,
      } = measurementDefinition;

      return {
        ...baseDefaults,
        title: titleOfActivity,
        description,
        organizationName,
        numberOfParticipants,
        causeOptions: mapCauseOptions(causeOptions),
        isRemote,
        location: address
          ? {
              street: address.street,
              lat: address.location.coordinates[1],
              lng: address.location.coordinates[0],
            }
          : undefined,
      };
    }, [measurementDefinition, measurementDefaultValues]);

  const sectionInfo = useMemo(() => sections[sectionIndex], [sectionIndex]);

  const causeOptions = useMemo(
    () =>
      mapCauseOptions(
        causeOptionsData?.map((cause) => ({
          value: cause._id,
          label: cause.displayName,
        })) ?? [],
      ),
    [causeOptionsData],
  );

  // Handlers
  const handleCancel = () => {
    if (sectionIndex === 0 || params.definitionId) {
      navigate(-1);
      return;
    }
    setSectionIndex((prev) => prev - 1);
  };

  const onCreate = async (data: IOffPlatformDefinitionFormFields) => {
    const {
      measurementDate,
      measurements,
      causeOptions: selectedCauses,
    } = data;

    const causes = buildCauseOptions(
      selectedCauses.map((opt) => opt.value),
      causeOptionsData,
    );

    const definitionData = {
      titleOfActivity: data.title,
      organizationName: data.organizationName,
      description: data.description,
      address: getAddressFromLocation(data.location),
      ecosystemId: String(selectedUserProfile?.ecosystem),
      userId: String(selectedUserProfile?.user),
      causeOptions: causes,
      appId: null,
      numberOfParticipants: data.numberOfParticipants,
      isRemote: !!data.isRemote,
      nameofParticipants: data.nameofParticipants,
    };

    const measurementsDto = createMeasurementsDto(
      measurements,
      measurementDate,
      definitionData,
      causes,
    );

    const createMeasurementDefinitionData: CreateOffPlatformMeasurementDefinitionDTO =
      {
        ...data,
        titleOfActivity: data.title,
        address: getAddressFromLocation(data.location),
        ecosystemId: String(selectedUserProfile?.ecosystem),
        userId: String(selectedUserProfile?.user),
        measurements: measurementsDto,
        causeOptions: mapCauseOptionsToDto(causes),
      };

    await MeasurementDefinitionService.createOffPlatform(
      createMeasurementDefinitionData,
    );
    goToRoute(PAGES.OffplatformActivitySuccess);
  };

  const onLog = async (data: IOffPlatformDefinitionFormFields) => {
    if (!measurementDefinition) return;

    const { measurementDate, measurements } = data;
    const newMeasurements = measurements.filter((m) => !!m.amount);

    const newMeasurementsDto = newMeasurements.map(
      ({ amount, measurementUnit }) => ({
        appId: null,
        typeOfActivity: measurementDefinition.typeOfActivity as ActivityType,
        titleOfActivity: measurementDefinition.titleOfActivity ?? '',
        organizationName: measurementDefinition.organizationName ?? '',
        description: measurementDefinition.description ?? '',
        address: measurementDefinition.address ?? null,
        ecosystemId: String(selectedUserProfile?.ecosystem),
        userId: String(selectedUserProfile?.user),
        causeOptions:
          measurementDefinition.causeOptions as unknown as ICategoryDTO[],
        isRemote: measurementDefinition.isRemote,
        measurementUnitId: measurementUnit._id,
        measurementDate,
        amount,
      }),
    );

    await MeasurementDefinitionService.addOffPlatformMeasurement(
      String(params?.definitionId),
      {
        measurements: newMeasurementsDto,
      },
    );

    goToRoute(PAGES.OffplatformActivitySuccess);
  };

  const handleSubmit = async (data: IOffPlatformDefinitionFormFields) => {
    const { measurements } = data;
    const isAllMeasurementsEmpty = measurements.every((m) => !m.amount);

    if (isAllMeasurementsEmpty) {
      toast.error('At least one measurement is required');
      return;
    }

    try {
      setIsLoading(true);
      if (measurementDefinition) {
        await onLog(data);
      } else {
        await onCreate(data);
      }
    } catch {
      toast.error('An error occurred when trying to log activity.');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    params,
    isLoading,
    sectionIndex,
    causeOptions,
    measurementDefinition,
    isLoadingMeasurementDefinition,
    measurementUnitGroupedOptions,
    definitionDefaultValues,
    sectionInfo,
    goToRoute,
    handleCancel,
    handleSubmit,
  };
};
